import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { propType } from 'graphql-anywhere'
import { useHistory } from 'react-router-dom'
import { useMutation } from 'react-apollo'

import { ACCOUNT_SCREENS } from '../../../constants'
import GrayLine from '../../shared/gray-line'
import Button from '../../button'
import Checkbox from '../../forms/checkbox'
import Input from '../../forms/input'
import SUBMIT_CANCEL_SURVEY_MUTATION from '../../../../graphql/mutations/submit-cancel-survey.gql'
import CancelSurveyOptionFields from '../../../../graphql/fragments/cancel-survey-option.gql'
import styles from '../cancel-subscription.css'

function CancelSurvey({ options }) {
  const history = useHistory()

  const [selectedOptions, setSelectedOptions] = useState([])
  const [freeTextValues, setFreeTextValues] = useState({})
  const [freeTextErrors, setFreeTextErrors] = useState({})

  // Submit cancel survey mutation
  // Support only a single free text field for now
  // Multiple free text options can be considered later based on discussion
  const [submitCancelSurvey, { loading: loadingSubmit }] = useMutation(SUBMIT_CANCEL_SURVEY_MUTATION)

  const onClickHandler = () => {
    const errors = {}

    // Validate required free text fields for selected options
    options.forEach((option) => {
      if (option.isFreeText && selectedOptions.includes(option.id)) {
        const value = (freeTextValues[option.id] || '').trim()
        if (!value) {
          errors[option.id] = 'Required field, please specify.'
        }
      }
    })

    // If there are validation errors, show them and stop submission
    if (Object.keys(errors).length > 0) {
      setFreeTextErrors(errors)
      return
    }

    const getSurveyInput = {
      input: {
        optionIds: selectedOptions,
        freeText: Object.values(freeTextValues).find((val) => val.trim()) || null
      }
    }

    submitCancelSurvey({ variables: getSurveyInput })
      .then(() => {
        history.replace(ACCOUNT_SCREENS.SUBSCRIPTION_PAYMENT.path)
      })
  }

  const handleCheckboxChange = (optionId) => {
    setSelectedOptions((prevSelected) => {
      const isSelected = prevSelected.includes(optionId)
      const hasFreeText = !!(freeTextValues[optionId] && freeTextValues[optionId].trim())

      // If free text exists, do not allow uncheck
      if (isSelected && hasFreeText) {
        return prevSelected
      }

      // Uncheck: remove option
      if (isSelected) {
        // clear error
        setFreeTextErrors((prev) => {
          const updated = { ...prev }
          delete updated[optionId]
          return updated
        })
        return prevSelected.filter((id) => id !== optionId)
      }

      // Check: add once (no duplicates)
      return [...prevSelected, optionId]
    })
  }

  const handleTextChange = (optionId) => (e) => {
    const value = e.target.value

    setFreeTextValues((prev) => ({
      ...prev,
      [optionId]: value
    }))

    setFreeTextErrors((prev) => ({
      ...prev,
      [optionId]: null
    }))

    setSelectedOptions((prevSelected) => {
      if (!prevSelected.includes(optionId)) {
        return [...prevSelected, optionId]
      }
      return prevSelected
    })
  }

  const hasOptions = options.length > 0
  const cta = selectedOptions.length > 0 ? 'SUBMIT' : 'DONE'

  return (
    <div className={classnames(styles.section, styles.borderCard)}>
      {hasOptions && (
        <>
          <div className={classnames(styles.header, styles.surveyHeader)}>
            Why did you cancel your subscription with us?
          </div>
          <div className={styles.copy}>
            We&apos;re always improving and appreciate any feedback you have.
          </div>
          <GrayLine />
          <div className={styles.optionList}>
            {options.map((option) => (
              <div key={option.id}>
                <Checkbox
                  wrapperClassName={styles.checkboxWrapper}
                  labelClassName={styles.checkboxLabelText}
                  id={option.id}
                  isChecked={selectedOptions.includes(option.id)}
                  label={option.label}
                  onChange={() => handleCheckboxChange(option.id)}
                  value={option.id}
                />
                {option.isFreeText && (
                  <Input
                    inputClass={styles.input}
                    name={option.id}
                    value={freeTextValues[option.id]}
                    onChange={handleTextChange(option.id)}
                    warning={freeTextErrors[option.id]}
                    exclamationClass={styles.noExclamation}
                  />
                )}
              </div>
            ))}
          </div>
          <GrayLine />
        </>
      )}
      <div className={styles.copy}>
        <span className={styles.bold}>
          Thank you. We hope we&apos;ll see you back soon.
        </span>
      </div>
      <Button
        className={styles.button}
        text={cta}
        onClick={onClickHandler}
        disabled={loadingSubmit}
      />
    </div>
  )
}

CancelSurvey.propTypes = {
  options: PropTypes.arrayOf(propType(CancelSurveyOptionFields)).isRequired
}

export default CancelSurvey
